export const ORDER_STATUSES = ['Bestellt', 'Reviewed', 'Eingereicht', 'Erstattet', 'Storno'];
export const statusKey = value => String(value || '').trim().toLocaleLowerCase('de-DE');
export const isSubmitted = order => statusKey(order.status) === 'eingereicht';
export const isNotSubmitted = order => ['bestellt', 'reviewed'].includes(statusKey(order.status));
export const isRefunded = order => statusKey(order.status) === 'erstattet';
export const isOpen = order => isSubmitted(order) || isNotSubmitted(order);
export const formatDate = value => value && Number.isFinite(Date.parse(value)) ? new Date(value).toLocaleDateString('de-DE') : '—';
export function formatMoney(value) {
  if (value === null || value === undefined || value === '' || !Number.isFinite(Number(value))) return '—';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(value));
}
export function safeLink(value) {
  if (!value) return null;
  try { const url = new URL(value); return ['https:', 'http:'].includes(url.protocol) ? url.href : null; }
  catch { return null; }
}
export const matchesAgent = (order, agent) => statusKey(order.agent_name) === statusKey(agent.display_name);
export function sumAmounts(orders) {
  if (orders.some(o => o.betrag == null || o.betrag === '' || !Number.isFinite(Number(o.betrag)))) return null;
  return orders.reduce((total, order) => total + Number(order.betrag), 0);
}
export function orderSummary(orders) {
  const notSubmitted = orders.filter(isNotSubmitted), submitted = orders.filter(isSubmitted), refunded = orders.filter(isRefunded);
  return { total: orders.length, notSubmitted, submitted, refunded, open: orders.filter(isOpen),
    openAmount: sumAmounts(orders.filter(isOpen)), notSubmittedAmount: sumAmounts(notSubmitted),
    submittedAmount: sumAmounts(submitted), refundedAmount: sumAmounts(refunded) };
}
export function submittedClipboard(orders, withLinks = true) {
  return orders.filter(isSubmitted).map(order => {
    const row = [order.order_date ? formatDate(order.order_date) : '-', order.order_number || '-', order.asin || '-'];
    if (withLinks) row.push(order.review_url || '-');
    return row.join('\t');
  }).join('\n');
}
export function filterAgents(agents, targets, { query = '', active = 'all', onlyOpen = false, wechat = 'all' }) {
  const search = statusKey(query);
  return agents.filter(agent => {
    const target = targets?.agents.find(a => a.id === agent.id);
    if (active === 'active' && !agent.active) return false;
    if (active === 'inactive' && agent.active) return false;
    if (onlyOpen && Number(agent.open_count || 0) === 0) return false;
    if (wechat !== 'all' && target?.status !== wechat) return false;
    return !search || statusKey([agent.display_name, agent.agent_id, ...(agent.aliases || []), agent.messenger_id,
      target?.target?.target_username, target?.session?.chat_name, target?.target?.target_name].filter(Boolean).join(' ')).includes(search);
  });
}

// Read every advertised page. Never silently truncate a history or copy only the
// first 500 orders. A moving/inconsistent snapshot fails visibly and can be retried.
export async function loadAgentOrders(api, agent, { signal, onProgress = () => {} } = {}) {
  const collected = [], ids = new Set(); let expectedTotal, expectedPages;
  for (let page = 1; page <= (expectedPages || 1); page++) {
    if (signal?.aborted) throw Object.assign(new Error('Abgebrochen'), { name: 'AbortError' });
    const response = await api.get('/orders', { params: { agent: agent.display_name, agentExact: true, limit: 100, page }, signal });
    if (signal?.aborted) throw Object.assign(new Error('Abgebrochen'), { name: 'AbortError' });
    const { orders, total, pages } = response.data || {};
    if (!Array.isArray(orders) || !Number.isSafeInteger(total) || total < 0 || !Number.isSafeInteger(pages) || pages < 0 || (total > 0 && pages < 1)) {
      throw new Error('Die API hat keinen vollständigen Bestellverlauf bestätigt. Bitte neu laden.');
    }
    if (page === 1) { expectedTotal = total; expectedPages = pages; }
    if (total !== expectedTotal || pages !== expectedPages) throw new Error('Der Bestellbestand wurde während des Ladens geändert. Bitte neu laden.');
    for (const order of orders) {
      if (order.id == null || ids.has(String(order.id))) throw new Error('Die Bestellseiten sind nicht eindeutig. Bitte neu laden.');
      ids.add(String(order.id)); collected.push(order);
    }
    if (collected.length > expectedTotal || (!orders.length && collected.length < expectedTotal)) throw new Error('Der Bestellverlauf ist unvollständig. Bitte neu laden.');
    onProgress({ loaded: collected.length, total: expectedTotal });
  }
  if (collected.length !== expectedTotal) throw new Error('Es fehlen Bestellungen. Bitte neu laden.');
  // Also safe with an older backend that still uses the legacy substring filter.
  return collected.filter(order => matchesAgent(order, agent));
}
